import React, { useEffect, useState } from "react";
import AdminGrid, { direction } from "../AdminGrid/AdminGrid";
import { columnTypes } from "../AdminGrid/AdminGrid";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { orderBy } from "@progress/kendo-data-query";
import { get } from "../../Common/api";
import { useDispatch } from "react-redux";
import Alert, { getNotificationForError } from "../Alert";
import { setCurrentPageNotification } from "../../redux/notificationSlice";
import { IsAdultFilterCell } from "../AdminGrid/CustomBooleanFilterCells";

export default function CustomerSearchPopup(props) {
  const {
    selectedCustomerIds = [-1],
    toggleDialog,
    customerSelectionCallback,
    isMultiSelectionEnabled = false,
  } = props;
  const [customers, setCustomers] = useState([]);
  const dispatch = useDispatch();
  const selectionColumnType = isMultiSelectionEnabled
    ? columnTypes.checkbox
    : columnTypes.radioButton;
  const [sort, setSort] = useState([
    { field: "lastName", dir: direction.ASC },
  ]);
  const [filter, setFilter] = useState([]);
  const [isAdultSort, setIsAdultSort] = useState(direction.ASC);

  const filterOperators = {
    text: [
      {
        text: "grid.filterStartsWithOperator",
        operator: "startswith",
      },
    ],
  };


  const onCustomerSelectionChange = (props) => {
    const selectedCustId = props.dataItem.id;
    const value = !props.dataItem.selectedCustomer.value;

    if (!isMultiSelectionEnabled) {
      const newCustomers = customers.map((cust) => ({
        ...cust,
        selectedCustomer: cust.id === selectedCustId ? true : false,
      }));

      setCustomers(newCustomers);
    } else {
      const newCustomers = customers.map((cust) =>
        cust.id === selectedCustId
          ? {
              ...cust,
              selectedCustomer: value,
            }
          : cust
      );
      setCustomers(newCustomers);
    }
  };

  const onOk = () => {
    const selectedCustomers = customers.filter((c) => c.selectedCustomer);
    customerSelectionCallback(selectedCustomers);
    toggleDialog();
  };

  const sortChange = (event) => {
    let sort = event.sort;
    let lastIndex = sort.length - 1;
    let currentField = sort[lastIndex].field;

    //We need to invert sort order to show first active users when ASC.
    if (currentField === "isAdult"){
      sort[lastIndex].dir = isAdultSort === direction.ASC ? direction.DESC : direction.ASC;
      setIsAdultSort(sort[lastIndex].dir);
    }

    setSort(sort);
  };

  const searchChange = (event) => {
    let newFilter = {
      logic: "or",
      filters: [
        {
          field: "userID",
          operator: "startswith",
          value: event.target.value,
        },
        {
          field: "lastName",
          operator: "startswith",
          value: event.target.value,
        },
        {
          field: "firstName",
          operator: "startswith",
          value: event.target.value,
        },
        {
          field: "middle",
          operator: "startswith",
          value: event.target.value,
        },
        {
          field: "grade",
          operator: "startswith",
          value: event.target.value,
        },
        {
          field: "homeroom",
          operator: "startswith",
          value: event.target.value,
        },
        {
          field: "school",
          operator: "startswith",
          value: event.target.value,
        }
      ],
    };
    
    setFilter(event.target.value == ""? [] : newFilter);
  };

  const columns = [
    {
      field: "selectedCustomer",
      title: " ",
      type: selectionColumnType,
      ariaLabelledByField: "userID",
      isFilterable: false,
      sortable : false,
      onChange: (event) => {},
    },
    {
      field: "userID",
      title: "User ID",
      isFilterable: true,
    },
    {
      field: "lastName",
      title: "Last Name",
      isFilterable: true,
    },
    {
      field: "firstName",
      title: "First Name",
      isFilterable: true,
    },
    {
      field: "middle",
      title: "Mid. Initial",
      isFilterable: true,
    },
    {
      field: "isAdult",
      title: "Adult",
      type: columnTypes.boolean,
      filterCell: IsAdultFilterCell,
      isFilterable: true,
    },
    {
      field: "grade",
      title: "Grade",
      isFilterable: true,
    },
    {
      field: "homeroom",
      title: "Homeroom",
      isFilterable: true,
    },
    {
      field: "school",
      title: "School",
      isFilterable: true,
    },
  ];

  useEffect(() => {
    let mounted = true;
    async function fetchData() {
      try {
        const result = await get("/customers");
        if (result) {
          if (!mounted) return;
          setCustomers(
            result.map((cust) => ({
              ...cust,
              isAdult: !cust.isStudent,
              selectedCustomer:
                selectedCustomerIds.indexOf(cust.id) >= 0 ? true : false,
            }))
          );
        }
      } catch (error) {
        dispatch(setCurrentPageNotification(getNotificationForError(error)));
      }
    }
    fetchData();
    return () => (mounted = false);
  }, [selectedCustomerIds, dispatch]);

  const listData = orderBy(
    customers.map((c) => ({
      ...c,
      grade: c.grade?.name,
      homeroom: c.homeroom?.name,
      school: c.school?.schoolName,
      actions: [],
      selectedCustomer: {
        value: c.selectedCustomer,
        onChange: onCustomerSelectionChange,
      },
    })),
    sort
  );

  return (
    <React.Fragment>
      <Dialog
        title={`Customer Search`}
        onClose={toggleDialog}
        width={"90%"}
        height={"90%"}
      >
        <Alert />
        <div className="col-md-12">
          <div
                className="form-group vf-insert-space col-md-3 col-md-offset-6"
                style={{ marginBottom: "5px" }}
              >
                <input
                  type="text"
                  aria-label="global search for available grid"
                  className="form-control"
                  placeholder="Search..."
                  data-testid="searchbox"
                  onChange={(event) =>
                    searchChange(event)
                  }
                />
                </div>
        </div>
        <AdminGrid
          anchorElement="target"
          position="top"
          columns={columns}
          data={listData}
          sortable={{ allowUnsort: false }}
          sort={sort}
          sortChange={sortChange}
          filter={filter}
          filterChange={setFilter}
          filterOperators={filterOperators}
          isFilterable={true}
        ></AdminGrid>
        <DialogActionsBar layout={"end"}>
          <input
            type="button"
            value="Close"
            aria-label="close"
            className="btn default btn-lg"
            onClick={toggleDialog}
          />
          <input
            type="button"
            value="Ok"
            aria-label="ok"
            className="btn blue btn-lg"
            onClick={onOk}
          />
        </DialogActionsBar>
      </Dialog>
    </React.Fragment>
  );
}
